/**
 * @file     useIsAdmin.ts
 * @purpose  Client hook returning the current user's workspace access. Reads
 *           user_profiles.is_admin + role via the browser Supabase client and
 *           derives the workspace (owner -> Agent Dashboard, partner ->
 *           Partner Portal) so every surface can link a role-holder to their
 *           own console. `isAdmin` stays owner-only for existing callers.
 */
'use client';

import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { getSupabaseBrowserClient } from '@/core/supabase/client';
import { isRole, isPartnerRole, destinationForRole, type Role } from '@/core/types/roles';

/** Where a role-holder's own console lives, and what to call it. */
export type Workspace = { href: string; label: string };

type Profile = { isAdmin: boolean; role: Role | null };

const EMPTY: Profile = { isAdmin: false, role: null };

const cache = new Map<string, Profile>();

/**
 * Mirrors the server's resolveRole precedence: an explicit role wins; a legacy
 * is_admin=true row with no role resolves to owner.
 */
function resolveClientRole(isAdminFlag: boolean, rawRole: unknown): Role | null {
  if (isRole(rawRole)) return rawRole;
  return isAdminFlag ? 'owner' : null;
}

function workspaceForRole(role: Role | null): Workspace | null {
  if (!role) return null;
  return {
    href: destinationForRole(role),
    label: isPartnerRole(role) ? 'Partner Portal' : 'Agent Dashboard',
  };
}

export function useIsAdmin(): {
  isAdmin: boolean;
  role: Role | null;
  workspace: Workspace | null;
  isLoading: boolean;
} {
  const { user, isLoading: authLoading } = useAuth();
  const [profile, setProfile] = useState<Profile>(() => (user?.id ? cache.get(user.id) ?? EMPTY : EMPTY));
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;
    if (authLoading) return;
    if (!user?.id) {
      setProfile(EMPTY);
      setIsLoading(false);
      return;
    }
    const cached = cache.get(user.id);
    if (cached) {
      setProfile(cached);
      setIsLoading(false);
      return;
    }
    (async () => {
      const sb = await getSupabaseBrowserClient();
      if (!sb) { if (!cancelled) { setIsLoading(false); } return; }
      const { data } = await (sb.from('user_profiles') as unknown as {
        select: (c: string) => {
          eq: (col: string, val: string) => {
            maybeSingle: () => Promise<{ data: { is_admin: boolean | null; role: string | null } | null }>;
          };
        };
      })
        .select('is_admin, role')
        .eq('user_id', user.id)
        .maybeSingle();

      const role = resolveClientRole(!!data?.is_admin, data?.role);
      const next: Profile = { isAdmin: role === 'owner', role };
      if (role) cache.set(user.id, next);
      else cache.delete(user.id);
      if (!cancelled) {
        setProfile(next);
        setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id, authLoading]);

  return {
    isAdmin: profile.isAdmin,
    role: profile.role,
    workspace: workspaceForRole(profile.role),
    isLoading,
  };
}
